import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Modal,
    ModalDialog,
} from '@mui/joy';
import { RestartAlt } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';
const POLL_INTERVAL_MS = 3000;
const FINISHED = ['succeeded', 'failed', 'expired'];

const statusText = (command) => {
    switch (command.status) {
        case 'pending':
            return 'Waiting for the hub to pick up the request…';
        case 'running':
            return 'Restarting hub…';
        case 'succeeded':
            return 'Hub restarted.';
        case 'failed':
            return command.error ? `Restart failed: ${command.error}` : 'Restart failed.';
        case 'expired':
            return 'The hub did not pick up the request within 10 minutes. It may be offline, or its firmware manager may need an update.';
        default:
            return command.status;
    }
};

const RestartHubButton = ({ hubId, onStatusChange }) => {
    const { session } = useAuth();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [command, setCommand] = useState(null);
    const [error, setError] = useState(null);
    const sessionRef = useRef(session);
    sessionRef.current = session;
    const onStatusChangeRef = useRef(onStatusChange);
    onStatusChangeRef.current = onStatusChange;

    const lastReportedRef = useRef(null);
    useEffect(() => {
        const key = command && `${command.id}:${command.status}`;
        if (key && key !== lastReportedRef.current) {
            lastReportedRef.current = key;
            onStatusChangeRef.current?.(command);
        }
    }, [command]);

    const request = (path, options = {}) => fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            Authorization: sessionRef.current?.accessToken,
            'Vendor-Id': sessionRef.current?.vendorId,
            'Content-Type': 'application/json',
        },
    });

    const commandId = command && !FINISHED.includes(command.status) ? command.id : null;
    useEffect(() => {
        if (!commandId) return undefined;
        const timer = setInterval(async () => {
            try {
                const res = await request(`/device/${hubId}/commands/${commandId}`);
                if (res.ok) setCommand(await res.json());
            } catch (e) {
                // Transient network failure; retry on the next tick.
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubId, commandId]);

    const restartHub = async () => {
        setConfirmOpen(false);
        setRequesting(true);
        setError(null);
        setCommand(null);
        try {
            const res = await request(`/device/${hubId}/commands`, {
                method: 'POST',
                body: JSON.stringify({ type: 'restart_services' }),
            });
            const body = await res.json().catch(() => null);
            if (res.status === 201) {
                setCommand(body);
            } else if (res.status === 409 && body?.command) {
                setCommand(body.command);
            } else {
                setError(body?.error || `Restart request failed (${res.status}).`);
            }
        } catch (e) {
            setError('Unable to reach the admin API.');
        } finally {
            setRequesting(false);
        }
    };

    const busy = requesting || Boolean(commandId);
    const failed = error || command?.status === 'failed' || command?.status === 'expired';
    const status = error || (command && statusText(command));

    return (
        <Box>
            <Button
                variant="outlined"
                color="warning"
                startDecorator={<RestartAlt />}
                disabled={busy}
                onClick={() => setConfirmOpen(true)}
            >
                Restart hub
            </Button>
            {status && (
                <Alert
                    variant="soft"
                    color={failed ? 'danger' : command?.status === 'succeeded' ? 'success' : 'neutral'}
                    startDecorator={busy ? <CircularProgress size="sm" /> : null}
                    sx={{ mt: 1 }}
                >
                    {status}
                </Alert>
            )}

            <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <ModalDialog role="alertdialog">
                    <DialogTitle>Restart hub?</DialogTitle>
                    <DialogContent>
                        All hub services restart. The owner loses live video and recording for about a minute.
                    </DialogContent>
                    <DialogActions>
                        <Button color="warning" onClick={restartHub}>Restart</Button>
                        <Button variant="plain" color="neutral" onClick={() => setConfirmOpen(false)}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Box>
    );
};

export default RestartHubButton;
